import React from "react";
import { motion } from "framer-motion";
import { FiArrowDown, FiArrowUpRight, FiDownload } from "react-icons/fi";
import DotField from "./DotField";
import Terminal from "./Terminal";
import Magnetic from "../ui/Magnetic";
import useLocalTime from "../../hooks/useLocalTime";
import { useIntro } from "../../context/IntroContext";
import { profile } from "../../data/resume";
import { ease } from "../../utils/motionPresets";
import portrait from "../../assets/portrait.webp";
import CV from "../../assets/Yaseen-Al-Midani.pdf";
import "./hero.css";

const lineReveal = {
  hidden: { y: "110%" },
  visible: (i) => ({ y: "0%", transition: { duration: 1.1, ease, delay: 0.35 + i * 0.1 } })
};

const fadeIn = (delay, ready) => ({
  initial: { opacity: 0, y: 18 },
  animate: ready ? { opacity: 1, y: 0 } : undefined,
  transition: { duration: 0.9, ease, delay }
});

const Hero = ({ onOpenPalette }) => {
  const time = useLocalTime(profile.timeZone);
  const { ready } = useIntro();
  const reveal = ready ? "visible" : "hidden";

  return (
    <section className="hero" id="top">
      <DotField />
      <div className="hero__glow" aria-hidden="true" />

      <div className="container hero__inner">
        <motion.div className="hero__top" {...fadeIn(0.2, ready)}>
          <span className="status-pill">
            <span className="status-pill__dot" />
            Available for new opportunities
          </span>
          <span className="hero__role mono">{profile.role}</span>
        </motion.div>

        <h1 className="hero__title">
          <span className="sr-only">
            {profile.name} — I build fast, secure &amp; scalable web products.
          </span>
          <span aria-hidden="true">
            <span className="hero__line">
              <motion.span className="hero__line-inner" variants={lineReveal} custom={0} initial="hidden" animate={reveal}>
                I build fast,
              </motion.span>
            </span>
            <span className="hero__line hero__line--indent">
              <motion.span className="hero__line-inner" variants={lineReveal} custom={1} initial="hidden" animate={reveal}>
                <span className="hero__portrait">
                  <img src={portrait} alt="" width="200" height="223" />
                </span>
                <span className="serif">secure</span> &amp; scalable
              </motion.span>
            </span>
            <span className="hero__line">
              <motion.span className="hero__line-inner" variants={lineReveal} custom={2} initial="hidden" animate={reveal}>
                web products<span className="accent">.</span>
              </motion.span>
            </span>
          </span>
        </h1>

        <div className="hero__grid">
          <motion.div className="hero__intro" {...fadeIn(0.8, ready)}>
            <p className="hero__lede">
              I'm <strong>{profile.name}</strong>, a software engineer in {profile.city}.{" "}
              {profile.summary}
            </p>
            <div className="hero__ctas">
              <Magnetic>
                <a href="#work" className="btn btn--primary">
                  View selected work <FiArrowUpRight />
                </a>
              </Magnetic>
              <Magnetic>
                <a href={CV} download="Yaseen-Al-Midani-CV.pdf" className="btn btn--ghost">
                  Download CV <FiDownload />
                </a>
              </Magnetic>
            </div>
          </motion.div>

          <motion.div
            className="hero__terminal"
            initial={{ opacity: 0, y: 40, rotateX: 12 }}
            animate={ready ? { opacity: 1, y: 0, rotateX: 0 } : undefined}
            transition={{ duration: 1.2, ease, delay: 0.9 }}
          >
            <Terminal start={ready} />
          </motion.div>
        </div>

        <motion.div className="hero__meta" {...fadeIn(1.2, ready)}>
          <span className="hero__meta-item">
            <span className="hero__meta-label">Based in</span> {profile.location}
          </span>
          <span className="hero__meta-item">
            <span className="hero__meta-label">Local time</span>
            <span className="mono">{time} GST</span>
          </span>
          <button type="button" className="hero__meta-item hero__meta-btn" onClick={onOpenPalette}>
            <span className="hero__meta-label">Navigate</span>
            <kbd>⌘</kbd>
            <kbd>K</kbd>
          </button>
          <a href="#about" className="hero__scroll" aria-label="Scroll to about section">
            <FiArrowDown />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
